import { ServerResponse } from "http";
import { asyncCall } from "./call";
import { NewAlarm } from "./types";
import { DXAlarm } from "@/types";
import { checkIfAllHoursAreSelected, generateFullWeeklyHourData, WeeklyHourData } from "@/components/library/WeeklyHourPicker/WeeklyHourPickerUtils";
import { apiV3Call } from "./apiV3Call";


type AlarmActiveHours = WeeklyHourData;

// Backend alarm interface (new format)
export interface BackendAlarm {
  id: number;
  enabled: boolean;
  name: string;
  device: number;
  index: number;
  subindex: number;
  varName: string;
  operator: string;
  limit: number;
  /** interval in minutes */
  interval: number;
  /** threshold in minutes */
  threshold: number;
  /**
   * comma-space seperated list of emails
   *
   * @example mailto: "email@example.com, other@example.com"
   **/
  mailto: string;
  comment: string;
  /**
   * timestamp in milliseconds of the time, the condition is met for the alert
   *
   * `0` if condition not met
   **/
  conditionMetSince: number;
  /**
   * timestamp in milliseconds of the time, the condition and the theshold are met.
   *
   * `0` if currently not alarming, timestamp
   **/
  alarmingSince: number;
  /**
   * timestamp in milliseconds
   *
   * `1483228800000` (2017-01-01 00:00:00.000 UTC) if the alarm has never been activated yet
   */
  blockedUntil: number;
  activeHours: AlarmActiveHours;
}

/**
 * Payload format expected by the API when saving an alarm.
 */
interface BackendAlarmUpsert {
  id?: number;
  enabled: boolean;
  name: string;
  device: number;
  index: number;
  subindex: number;
  operator: string;
  limit: number;
  interval: number;
  threshold: number;
  mailto: string;
  comment: string;
  activeHours: AlarmActiveHours;
}

export class AlarmsConnector {
  private readonly sessionID: string;
  private readonly urlPrefix: string;

  constructor(urlPrefix: string, sessionID: string) {
    this.urlPrefix = urlPrefix;
    this.sessionID = sessionID;
  }

  /**
   * Ensure we always have all days and arrays, and return a fresh object (no shared refs).
   */
  private normalizeWeeklyHours(weeklyHourData?: WeeklyHourData): WeeklyHourData {
    const emptyWeeklyHourData = generateFullWeeklyHourData();

    const src = weeklyHourData ?? emptyWeeklyHourData;

    return {
      mon: [...(src.mon ?? [])],
      tue: [...(src.tue ?? [])],
      wed: [...(src.wed ?? [])],
      thu: [...(src.thu ?? [])],
      fri: [...(src.fri ?? [])],
      sat: [...(src.sat ?? [])],
      sun: [...(src.sun ?? [])],
    };
  }

  /**
   * Transform backend alarm to frontend DXAlarm format
   */
  private transformBackendAlarmToFrontend(backendAlarm: BackendAlarm): DXAlarm {
    const normalized = this.normalizeWeeklyHours(backendAlarm.activeHours);
    const alarm: DXAlarm = {
      id: backendAlarm.id,
      active: backendAlarm.enabled,
      alarming: backendAlarm.alarmingSince > 0 ? 1 : 0,
      alarmingSince: new Date(backendAlarm.alarmingSince).toISOString(),
      alarmname: backendAlarm.name,
      autogenerated: false, // Not provided by backend, default to false
      comment: backendAlarm.comment,
      threshold: backendAlarm.threshold,
      condition: backendAlarm.operator,
      deviceid: backendAlarm.device,
      interval: backendAlarm.interval,
      limit: backendAlarm.limit,
      mailto: backendAlarm.mailto,
      operator: backendAlarm.operator,
      sppindex: backendAlarm.index,
      sppsubindex: backendAlarm.subindex,
      variable: backendAlarm.varName,
    };

    // Only include customTime if it's NOT full 0..23 to have proper UI behavior
    if (!checkIfAllHoursAreSelected(normalized)) {
      alarm.customTime = normalized;
    }

    return alarm;
  }

  /**
   * Transform frontend NewAlarm to backend format
   */
  private transformFrontendAlarmToBackend(alarm: NewAlarm): BackendAlarmUpsert {
    return {
      id: alarm.id || undefined,
      device: alarm.device,
      index: alarm.index,
      subindex: alarm.subindex,
      name: alarm.name,
      // varName is NOT sent - API V3 calculates it from device_variables table
      operator: alarm.operator,
      limit: alarm.limit,
      interval: alarm.interval,
      threshold: alarm.threshold,
      mailto: alarm.mailto,
      comment: alarm.comment || "",
      enabled: Boolean(alarm.active),
      activeHours: this.normalizeWeeklyHours(alarm.customTime),
    };

  }

  /**
   * Gets the alarmID and deletes the alarm.
   * @target "/api/delete-alarm"
   * @param alarmID as options.id
   * @returns
   */
  async deleteAlarm(options: { id: number }) {
    const result: ServerResponse = await asyncCall(this.urlPrefix + "/api/delete-alarm", {
      session: this.sessionID,
      ...options,
    });
    return result;
  }

  /**
   * Gets alarms from the server.
   * @target "/api/v3.0/get-alarms"
   * @returns
   */
  async getAlarms() {
    // API V3 uses session cookies, not session in body
    // GetAlarmsParams is an empty struct, so we send an empty object
    const result = await apiV3Call<Record<string, never>, { alarms: BackendAlarm[] }>(
      this.urlPrefix + "/api/v3.0/get-alarms",
      {} // Empty body - session is handled via cookies
    );

    // Check if the call was successful
    if (!result.success) {
      console.error("Failed to get alarms:", result.error);
      return [];
    }

    // Transform backend alarms to frontend format
    return result.data.alarms.map((alarm: BackendAlarm) => {
      return this.transformBackendAlarmToFrontend(alarm);
    });
  }

  /**
   * This function is used for creating alarms.
   * When you create a new alarm, options.id is not needed.   *
   * @target "/api/save-alarm"
   * @param options.id undefined
   * @param options.index
   * @param options.device
   * @param options.subindex
   * @param options.limit
   * @param options.interval
   * @param options.threshold
   * @param options.name
   * @param options.varname
   * @param options.operator
   * @param options.mailto
   * @param options.comment optional
   * @param options.active 1/0
   * @param options.autogenerated 1/0
   * @returns
   */
  async createAlarm(options: NewAlarm) {
    // Transform frontend format to backend format
    const backendAlarm = this.transformFrontendAlarmToBackend(options);

    // API V3 expects { alarm: {...} } format
    const result = await apiV3Call<{ alarm: BackendAlarmUpsert }, { alarmID: number }>(
      this.urlPrefix + "/api/v3.0/save-alarm",
      { alarm: backendAlarm }
    );

    if (!result.success) {
      console.error("Failed to create alarm:", result.error);
      throw new Error(result.error.messageKey || "Failed to create alarm");
    }

    return result.data.alarmID;
  }

  /**
   * This function is used for updating alarms.
   * ID is required. If you do not pass an id, it will create a new alarm.
   * @target "/api/save-alarm"
   * @alias createAlarm()
   * @param options.id
   * @param options.index
   * @param options.device
   * @param options.subindex
   * @param options.limit
   * @param options.interval
   * @param options.threshold
   * @param options.name
   * @param options.varname
   * @param options.operator
   * @param options.mailto
   * @param options.comment optional
   * @param options.active 1/0
   * @param options.autogenerated 1/0
   * @returns
   */
  async updateAlarm(options: NewAlarm) {
    return this.createAlarm(options);
  }
}
